import { useCallback, useState } from 'react';
import { useSimActions } from '../../../hooks';
import { ComponentSize } from '../../../types';
import { conditionalObject } from '../../../utils';
import { Icon, Icons } from '../Icons';
import { FavoriteIconProps } from './FavoriteIcon.types';

const FavoriteIcon = ({ isFavorited, personaId, size = ComponentSize.SMALL, onSuccess }: FavoriteIconProps) => {
  const [isStarHovered, setIsStarHovered] = useState(false);

  const { runFavorite, runUnfavorite, isFavoriteLoading, isUnfavoriteLoading } = useSimActions();

  const clickable = !!personaId && !!onSuccess;

  const getFillColor = () => {
    if (isStarHovered) {
      return isFavorited ? undefined : 'fill-orange-500';
    } else {
      return isFavorited ? 'fill-orange-500' : undefined;
    }
  };

  const onClick = useCallback(async () => {
    // Prevent multiple clicks.
    if (isFavoriteLoading || isUnfavoriteLoading || !clickable) return;
    // Run the appropriate mutation based on the current favorite status.
    if (isFavorited) {
      runUnfavorite({ id: personaId, onSuccess: () => onSuccess(false) });
    } else {
      runFavorite({ id: personaId, onSuccess: () => onSuccess(true) });
    }
  }, [
    isFavorited,
    isFavoriteLoading,
    isUnfavoriteLoading,
    personaId,
    runFavorite,
    runUnfavorite,
    clickable,
    onSuccess,
  ]);

  const buttonHandlers = {
    ...conditionalObject(clickable, {
      tooltip: isFavorited ? 'Remove from favorites' : 'Add to favorites',
      onClick,
      onHover: (hover: boolean) => setIsStarHovered(hover),
    }),
  };

  if (!clickable && !isFavorited) return null;

  return (
    <Icons icon={Icon.STAR} color="stroke-orange-500" fillColor={getFillColor()} size={size} {...buttonHandlers} />
  );
};

export default FavoriteIcon;
